import { Component } from "@theme/component";
import {
  expandFlavors,
  offerVariants,
  packCount,
  optionControls,
  matchVariant,
  allocationFor,
  stockState,
  normalizeOffers,
  summaryDetails,
  initialSelections,
} from "@theme/product-purchase-model";

const node = (tag, className, text) => {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text != null) element.textContent = text;
  return element;
};

/** A form-local controller; multiple PDP/landing-page instances stay independent. */
class ProductPurchaseBox extends Component {
  #abort;
  #source;
  #data;
  #offers = [];
  #offer;
  #supply = "";
  #selections = {};
  #memories = new Map();
  #variant;
  #allocation;
  #stock;
  #form;
  #widgetObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize(true);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#widgetObserver?.disconnect();
    if (this.#form?.purchaseController === this)
      delete this.#form.purchaseController;
    this.#source = null;
    super.disconnectedCallback();
  }

  #initialize(force = false) {
    const source = this.querySelector("[data-purchase-data]")?.textContent;
    if (
      !source ||
      (!force &&
        source === this.#source &&
        this.#abort &&
        !this.#abort.signal.aborted)
    )
      return;
    this.#abort?.abort();
    try {
      this.#data = JSON.parse(source);
    } catch {
      return;
    }
    this.#source = source;
    this.#form = this.closest("form");
    if (!this.#form) return;
    this.#abort = new AbortController();
    const scope = this.closest(".shopify-section");
    const removeLegacyWidgets = () => {
      scope
        ?.querySelectorAll(".appstle_sub_widget")
        .forEach((widget) => widget.remove());
    };
    this.#widgetObserver?.disconnect();
    this.#widgetObserver = new MutationObserver(removeLegacyWidgets);
    if (scope)
      this.#widgetObserver.observe(scope, { childList: true, subtree: true });
    removeLegacyWidgets();
    this.#offers = normalizeOffers(this.#data);
    const initial =
      this.#data.product.variants.find(
        (v) => String(v.id) === String(this.#data.selectedVariant),
      ) || this.#data.product.variants[0];
    this.#offer =
      this.#offers.find((o) => o.key === this.#data.defaultOffer) ||
      this.#offers[0];
    const supplyIndex = this.#data.product.optionNames.findIndex(
      (name) =>
        ["supply", "pack"].includes(this.#data.optionSettings?.[name]?.role) ||
        name.toLowerCase() === "supply",
    );
    this.#supply = this.#offer?.supply || initial?.options[supplyIndex] || "";
    if (this.#data.explicitVariant && supplyIndex >= 0) {
      this.#supply = initial.options[supplyIndex];
      this.#offer =
        this.#offers.find(
          (offer) =>
            offer.mode === this.#offer.mode && offer.supply === this.#supply,
        ) || this.#offer;
    }
    // Shopify's selected allocation is a deliberate deep link and takes precedence.
    if (this.#data.selectedPlan) {
      this.#offer =
        this.#offers.find(
          (o) =>
            String(o.planId) === String(this.#data.selectedPlan) &&
            (!o.supply || o.supply === initial.options[supplyIndex]),
        ) || this.#offer;
      this.#supply = initial.options[supplyIndex] || "";
    }
    this.#selections = initialSelections(this.#data, initial);
    this.#form.purchaseController = this;
    this.dataset.purchaseSupplyActive = String(
      this.#data.product.variants.every(
        (variant) => variant.quantityMin <= 1 && variant.quantityIncrement <= 1,
      ),
    );
    const fallback = this.#form.querySelector(
      "[data-native-variant], [data-purchase-native-variant]",
    );
    if (fallback) {
      fallback.disabled = true;
      fallback.closest("label").hidden = true;
    }
    const id = this.#form.querySelector(
      '[data-purchase-id], input[ref="variantId"]',
    );
    id.name = "id";
    id.disabled = false;
    this.addEventListener(
      "click",
      (event) => {
        const button = event.target.closest("button[data-option]");
        if (button) this.#change({ target: button });
      },
      { signal: this.#abort.signal },
    );
    document.addEventListener(
      "purchase:gallery-ready",
      () => this.#dispatchGallery(),
      { signal: this.#abort.signal },
    );
    this.addEventListener("change", this.#change, {
      signal: this.#abort.signal,
    });
    this.addEventListener("purchase:addon-change", () => this.#render(), {
      signal: this.#abort.signal,
    });
    this.#render();
  }

  #money = (price) =>
    new Intl.NumberFormat(this.#data.product.locale || "en", {
      style: "currency",
      currency: this.#data.product.currency,
      currencyDisplay: "narrowSymbol",
    }).format(price / 100);

  #selection(offer) {
    const variants = offerVariants(this.#data, offer, this.#supply);
    const packs = offer.packs || packCount(this.#data, variants[0]);
    const controls = optionControls(this.#data, variants, packs);
    const selections = {};
    for (const control of controls) {
      selections[control.index] = Array.from(
        { length: control.slots },
        (_, slot) => {
          const current = this.#selections[control.index] || [];
          const value = current[slot] || current[current.length - 1];
          return control.values.includes(value) ? value : control.values[0];
        },
      );
    }
    const variant = matchVariant(variants, controls, selections);
    return {
      variants,
      controls,
      selections,
      variant,
      packs,
      allocation: allocationFor(variant, offer),
    };
  }

  #change = (event) => {
    const target = event.target;
    if (!(
      target instanceof HTMLInputElement ||
      target instanceof HTMLSelectElement ||
      target instanceof HTMLButtonElement
    ))
      return;
    if (target.dataset.offer) {
      const oldKey =
        this.#offer.supply ||
        (this.#offer.followSupply ? this.#supply : "default");
      this.#memories.set(
        oldKey,
        Object.fromEntries(
          Object.entries(this.#selections).map(([key, values]) => [
            key,
            [...values],
          ]),
        ),
      );
      this.#offer = this.#offers.find((o) => o.key === target.dataset.offer);
      if (this.#offer.supply) this.#supply = this.#offer.supply;
      const key =
        this.#offer.supply ||
        (this.#offer.followSupply ? this.#supply : "default");
      if (this.#memories.has(key)) this.#selections = this.#memories.get(key);
    } else if (target.dataset.option != null) {
      this.#selections[target.dataset.option][Number(target.dataset.slot)] =
        target.value;
    } else return;
    const focusName = target.name;
    const focusValue = target.value;
    this.#render();
    [...this.querySelectorAll("input, select, button[data-option]")]
      .find(
        (el) =>
          el.name === focusName &&
          (el.type !== "radio" || el.checked) &&
          (el.type !== "button" || el.value === focusValue),
      )
      ?.focus({ preventScroll: true });
  };

  #render() {
    if (!this.#offer) return;
    const active = this.#selection(this.#offer);
    this.#selections = { ...this.#selections, ...active.selections };
    this.#variant = active.variant;
    this.#allocation = active.allocation;
    this.#stock = stockState(this.#data, this.#variant);
    const fragment = document.createDocumentFragment();
    const showCards =
      this.#offers.length > 1 || this.#offer.mode === "subscription";
    if (showCards)
      fragment.append(
        node("h2", "purchase-layout__heading", this.#data.labels.choose),
      );
    const group = node("div", "purchase-supply__plans");
    if (showCards) {
      group.setAttribute("role", "radiogroup");
      group.setAttribute("aria-label", this.#data.labels.choose);
    }
    for (const offer of this.#offers) {
      const selected = offer === this.#offer;
      const selection = selected ? active : this.#selection(offer);
      const price = selection.allocation?.price ?? selection.variant?.price;
      const compare = Math.max(
        selection.variant?.compareAtPrice || 0,
        selection.variant?.price || 0,
        selection.allocation?.compareAtPrice || 0,
      );
      const saving =
        price != null && compare > price
          ? Math.round(((compare - price) / compare) * 100)
          : 0;
      if (!showCards) continue;
      const wrapper = node("div", "purchase-plan-wrap");
      if (
        offer.mode === "onetime" &&
        this.#offers.some((o) => o.mode === "subscription")
      ) {
        const divider = node("div", "purchase-plan__or");
        divider.append(node("span", "", this.#data.labels.or));
        wrapper.append(divider);
      }
      const card = node(
        "article",
        `purchase-plan${selected ? " is-selected" : ""}`,
      );
      if (offer.badge)
        card.append(node("div", "purchase-plan__badge", offer.badge));
      const label = node("label", "purchase-plan__choice");
      const radio = node("input");
      radio.type = "radio";
      radio.name = `${this.dataset.formId}-offer`;
      radio.value = offer.key;
      radio.dataset.offer = offer.key;
      radio.checked = selected;
      const copy = node("span", "purchase-plan__copy");
      copy.append(
        node(
          "strong",
          "",
          offer.title ||
            (offer.mode === "onetime"
              ? this.#data.labels.onetime
              : offer.supply && offer.days
                ? `${offer.days}${this.#data.labels.supply}`
                : this.#data.labels.subscribe),
        ),
      );
      if (offer.supply || offer.days || offer.description)
        copy.append(
          node(
            "span",
            "",
            offer.description ||
              `${selection.packs} ${selection.packs === 1 ? this.#data.labels.pack : this.#data.labels.packs}${offer.days ? ` / ${offer.days} ${this.#data.labels.servings}` : ""}`,
          ),
        );
      const badges = node("span", "purchase-plan__highlights");
      if (saving)
        badges.append(
          node(
            "span",
            "purchase-plan__saving",
            `${this.#data.labels.save} ${saving}%`,
          ),
        );
      const hasGiftBadge =
        offer.giftBadge &&
        this.#gifts(offer).some((gift) => gift.key !== "competition");
      if (offer.popular || offer.inlineBadge || hasGiftBadge)
        badges.append(
          node(
            "span",
            "purchase-plan__popular",
            offer.popular
              ? `🔥 ${this.#data.labels.popular}`
              : hasGiftBadge
                ? `🎁 ${this.#data.labels.freeGifts}`
                : offer.inlineBadge,
          ),
        );
      if (badges.childElementCount) copy.append(badges);
      const pricing = node("span", "purchase-plan__pricing");
      const prices = node("span", "purchase-prices");
      if (saving) prices.append(node("s", "", this.#money(compare)));
      prices.append(
        node(
          "strong",
          "",
          price != null ? this.#money(price) : this.#data.labels.unavailable,
        ),
      );
      pricing.append(prices);
      const days =
        offer.days || (offer.followSupply ? selection.packs * 30 : 0);
      if (days && price != null)
        pricing.append(
          node("span", "", `${this.#money(price / days)}/${this.#data.unit}`),
        );
      label.append(radio, copy, pricing);
      card.append(label);
      if (selected && offer.mode === "subscription" && selection.allocation) {
        const details = node("div", "purchase-plan__details");
        const features = node("ul", "purchase-plan__features");
        features.setAttribute("role", "list");
        const pause = node("li", "", this.#data.labels.cancel);
        const tooltip = this.querySelector("[data-subscription-tooltip]")
          ?.content.firstElementChild;
        if (tooltip) pause.append(tooltip.cloneNode(true));
        features.append(
          node("li", "", this.#renewal(selection.allocation, offer)),
          pause,
        );
        if (offer.freeShipping)
          features.append(node("li", "", this.#data.labels.shipping));
        if (offer.protection !== false)
          features.append(node("li", "", this.#data.labels.protection));
        if (offer.guaranteeLabel) {
          const guarantee = node("li", "", offer.guaranteeLabel);
          const source = [
            ...this.querySelectorAll("[data-offer-guarantee]"),
          ].find((template) => template.dataset.offerGuarantee === offer.key);
          if (source?.content.firstElementChild)
            guarantee.append(source.content.firstElementChild.cloneNode(true));
          features.append(guarantee);
        }
        details.append(features);
        const gifts = this.#gifts(offer);
        if (gifts.length) {
          if (
            offer.competitionLabel &&
            gifts.some((gift) => gift.key === "competition")
          ) {
            const entry = node("li", "", offer.competitionLabel);
            const tooltip = this.querySelector("[data-competition-tooltip]")
              ?.content.firstElementChild;
            if (tooltip) entry.append(tooltip.cloneNode(true));
            features.append(entry);
          }
          const count = gifts.filter(
            (gift) => gift.key !== "competition",
          ).length;
          if (count)
            features.append(
              node(
                "li",
                "",
                this.#data.labels.giftsFirstOrder.replace(
                  "[count]",
                  String(count),
                ),
              ),
            );
          const giftRow = node("div", "purchase-plan__gifts");
          for (const gift of gifts) {
            giftRow.append(gift.template.content.cloneNode(true));
          }
          details.append(giftRow);
        }
        card.append(details);
      }
      wrapper.append(card);
      group.append(wrapper);
    }
    if (showCards) fragment.append(group);
    const choices = this.#renderOptions(active);
    if (choices.childElementCount) {
      if (
        !this.#data.giftCard &&
        !active.controls.every((control) => control.heading)
      )
        fragment.append(
          node(
            "h2",
            "purchase-layout__heading",
            active.controls.some((control) => /flavor/i.test(control.name))
              ? this.#data.labels.flavor
              : this.#data.labels.variant,
          ),
        );
      fragment.append(choices);
    }
    this.querySelector("[data-purchase-options]").replaceChildren(fragment);
    this.syncForm();
    const message = this.querySelector("[data-stock-message]");
    message.textContent =
      this.#stock.messages.join(" ") ||
      (this.#stock.soldOut ? this.#data.labels.soldOut : "");
    message.hidden = !message.textContent;
    const summary = this.querySelector("[data-purchase-summary]");
    const price = this.#allocation?.price ?? this.#variant?.price;
    const addon = this.querySelector("product-purchase-addon")?.summary?.();
    const firstOrderPrice = price == null ? price : price + (addon?.price || 0);
    const compare = Math.max(
      this.#variant?.compareAtPrice || 0,
      this.#variant?.price || 0,
      this.#allocation?.compareAtPrice || 0,
    );
    const heading = node("div", "purchase-summary__heading");
    if (showCards) heading.append(node("h2", "", this.#data.labels.summary));
    const prices = node("div", "purchase-prices");
    const firstOrderCompare = compare + (addon?.compare || 0);
    if (firstOrderCompare > firstOrderPrice)
      prices.append(node("s", "", this.#money(firstOrderCompare)));
    prices.append(
      node(
        "strong",
        "",
        firstOrderPrice != null
          ? this.#money(firstOrderPrice)
          : this.#data.labels.unavailable,
      ),
    );
    heading.append(prices);
    summary.classList.toggle("purchase-summary--simple", !showCards);
    summary.replaceChildren(showCards ? heading : prices);
    if (!this.#data.giftCard)
      for (const line of summaryDetails(this.#data, this.#offer, active))
        summary.append(node("p", "", line));
    if (this.#allocation) {
      const gifts = this.#gifts(this.#offer)
        .filter(({ key }) => key !== "competition")
        .map(({ template }) =>
          template.content.querySelector("[data-gift-product]"),
        )
        .filter(Boolean);
      if (gifts.length) {
        let giftText = (
          gifts.length === 1
            ? this.#data.labels.summaryGift
            : this.#data.labels.summaryGifts
        ).replace("[count]", String(gifts.length));
        if (
          gifts.every(
            (gift) =>
              gift.hasAttribute("data-gift-value") &&
              Number.isFinite(Number(gift.dataset.giftValue)),
          )
        ) {
          const value = gifts.reduce(
            (total, gift) => total + Number(gift.dataset.giftValue),
            0,
          );
          giftText = this.#data.labels.summaryGiftValue
            .replace("[gifts]", giftText)
            .replace("[value]", this.#money(value).replace(/([.,])00$/, ""));
        }
        const giftLine = node("p");
        giftLine.append(node("strong", "", giftText));
        summary.append(giftLine);
      }
    }
    if (this.#allocation)
      summary.append(
        node("p", "", this.#renewal(this.#allocation, this.#offer, true)),
      );
    else if (!this.#data.giftCard && price != null)
      summary.append(
        node(
          "p",
          "",
          this.#data.labels.oneTimeDelivery.replace(
            "[price]",
            this.#money(price),
          ),
        ),
      );
    if (addon) summary.append(node("p", "purchase-summary__addon", addon.text));
    const daily = this.closest(".shopify-section")?.querySelector(
      "[data-purchase-from]",
    );
    if (daily) {
      daily.textContent =
        this.#offer.days && price != null
          ? `${this.#data.labels.from} ${this.#money(price / this.#offer.days)}/${this.#data.unit}`
          : "";
      daily.hidden = !daily.textContent;
    }
    this.#dispatchGallery();
  }

  #renewal(allocation, offer = this.#offer, summary = false) {
    let cadence = offer.cadence || allocation.name;
    if (summary)
      cadence = cadence.charAt(0).toLocaleLowerCase() + cadence.slice(1);
    return (
      summary
        ? this.#data.labels.summaryAutoships
        : offer.renewalLabel || this.#data.labels.renewal
    )
      .replace(
        "[price]",
        this.#money(allocation.recurringPrice || allocation.price),
      )
      .replace("[cadence]", cadence)
      .replace("[days]", String(offer.days || 30));
  }

  #gifts(offer) {
    if (!offer.gifts || offer.mode !== "subscription") return [];
    const allowed = this.#data.marketOverrides?.[this.#data.country]?.gifts;
    return [...this.querySelectorAll("[data-purchase-gift-template]")]
      .map((template) => ({
        template,
        key: template.dataset.purchaseGiftTemplate,
      }))
      .filter(({ key }) =>
        key === "competition"
          ? offer.competition
          : !allowed || allowed.includes(key),
      );
  }

  #dispatchGallery() {
    if (
      this.closest(".shopify-section")?.querySelector("media-gallery") &&
      !this.#data.variantRules?.[this.#variant?.id]?.gallery?.length
    ) {
      this.dispatchEvent(
        new CustomEvent("kb-sub:option-change", {
          bubbles: true,
          detail: {
            id: this.#offer.key,
            kind: this.#offer.mode,
            days: this.#offer.days || packCount(this.#data, this.#variant) * 30,
            variantId: this.#variant?.id,
            mediaId: this.#variant?.mediaId,
            image: this.#variant?.image,
          },
        }),
      );
    }
    this.dispatchEvent(
      new CustomEvent("purchase:change", {
        bubbles: true,
        detail: {
          variant: this.#variant,
          variantId: this.#variant?.id,
          sellingPlanId: this.#allocation?.id,
          galleryVariantId:
            this.#data.variantRules?.[this.#variant?.id]?.galleryVariantId,
        },
      }),
    );
  }

  #renderOptions(selection) {
    const wrapper = node("div", "purchase-shared-options");
    for (const control of selection.controls) {
      for (let slot = 0; slot < control.slots; slot++) {
        const fieldset = node("fieldset", "purchase-flavors");
        fieldset.append(
          node(
            "legend",
            control.heading || this.#data.giftCard
              ? "purchase-layout__heading"
              : "",
            control.slots > 1
              ? `${control.heading ? `${control.label} — ` : ""}${this.#data.labels.pack} ${slot + 1}`
              : control.label,
          ),
        );
        if (this.#data.giftCard) {
          fieldset.classList.add("purchase-denominations");
          const choices = node("div", "purchase-supply__plans");
          for (const value of control.values) {
            const variant = matchVariant(
              selection.variants,
              selection.controls,
              {
                ...selection.selections,
                [control.index]: [value],
              },
            );
            const selected =
              selection.selections[control.index][slot] === value;
            const available =
              variant && !stockState(this.#data, variant).soldOut;
            const card = node(
              "div",
              `purchase-plan${selected ? " is-selected" : ""}${available ? "" : " is-unavailable"}`,
            );
            const label = node("label", "purchase-plan__choice");
            const radio = node("input");
            radio.type = "radio";
            radio.name = `${this.dataset.formId}-option-${control.index}-${slot}`;
            radio.value = value;
            radio.dataset.option = control.index;
            radio.dataset.slot = slot;
            radio.checked = selected;
            radio.disabled = !available;
            const copy = node("span", "purchase-plan__copy");
            copy.append(
              node("strong", "", variant ? this.#money(variant.price) : value),
            );
            if (!available)
              copy.append(node("span", "", this.#data.labels.soldOut));
            label.append(radio, copy);
            card.append(label);
            choices.append(card);
          }
          fieldset.append(choices);
        } else if (/flavor/i.test(control.name)) {
          const choices = node("div", "purchase-flavors__options");
          for (const value of control.values) {
            const button = node("button", "purchase-flavor");
            button.type = "button";
            button.value = value;
            button.name = `${this.dataset.formId}-option-${control.index}-${slot}`;
            button.dataset.option = control.index;
            button.dataset.slot = slot;
            button.setAttribute(
              "aria-pressed",
              String(selection.selections[control.index][slot] === value),
            );
            const art =
              this.#data.optionSettings?.[control.name]?.values?.[value]?.icon;
            if (art && this.#data.icons?.[art]) {
              const img = node("img");
              img.src = this.#data.icons[art];
              img.alt = "";
              img.width = 24;
              img.height = 24;
              button.append(img);
            }
            button.append(node("span", "", value));
            choices.append(button);
          }
          fieldset.append(choices);
        } else {
          const select = node("select");
          select.name = `${this.dataset.formId}-option-${control.index}-${slot}`;
          select.dataset.option = control.index;
          select.dataset.slot = slot;
          select.setAttribute("aria-label", control.label);
          for (const value of control.values) {
            const option = node("option", "", value);
            option.value = value;
            option.selected =
              selection.selections[control.index][slot] === value;
            select.append(option);
          }
          fieldset.append(select);
        }
        wrapper.append(fieldset);
      }
    }
    return wrapper;
  }

  selectionAvailable() {
    return Boolean(
      this.#variant &&
      !this.#stock.soldOut &&
      (this.#offer.mode !== "subscription" || this.#allocation),
    );
  }

  syncForm() {
    const available = this.selectionAvailable();
    this.#form.dataset.purchaseAvailable = String(available);
    this.#form.querySelector(
      '[data-purchase-id], input[ref="variantId"]',
    ).value = this.#variant?.id || "";
    let plan = this.#form.querySelector('input[name="selling_plan"]');
    if (!plan) {
      plan = node("input");
      plan.type = "hidden";
      plan.name = "selling_plan";
      this.#form.append(plan);
    }
    plan.value = this.#allocation?.id || "";
    plan.disabled = !this.#allocation;
    this.#form.querySelector('input[name="quantity"]').value =
      this.#variant?.quantityMin || 1;
    const button = this.#form.querySelector('button[name="add"]');
    if (button) {
      button.disabled =
        !available || this.#form.dataset.purchasePending === "true";
      const text = button.querySelector(".add-to-cart-text__content");
      if (text)
        text.textContent = available
          ? this.#data.labels.add
          : this.#variant
            ? this.#data.labels.soldOut
            : this.#data.labels.unavailable;
    }
  }
}

if (!customElements.get("product-purchase-box"))
  customElements.define("product-purchase-box", ProductPurchaseBox);
